/**
 * FiscalizacionCenter.jsx · v3.9.1 · Centro Maestro de Fiscalización
 * ═══════════════════════════════════════════════════════════════════
 * Módulo de primer nivel para el usuario admin/super_admin. Reemplaza
 * la sección del panel Cerebros. Tres pestañas:
 *  1. Reglas IA · matriz (FiscMatrixEditor completo, ya construido)
 *  2. Constructor de Formularios · define qué campos ve el técnico por
 *     tipo-hija de solución.
 *  3. Fotos de Referencia · uploader por regla para guiar al técnico.
 */
import { Fragment, useEffect, useMemo, useState, useRef, useCallback } from "react";
import Layout from "../components/Layout";
import FloatingPanel from "../components/ui/FloatingPanel";   // v101.98.0
import api from "../lib/api";
import { getSignedUrls } from "../lib/signedUrls";
import { toast } from "sonner";
import FiscMatrixEditor from "./FiscMatrixEditor";
import InfraRefillCard from "../components/InfraRefillCard"; // v101.45.0
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Switch } from "../components/ui/switch";
import {
  ClipboardList, Sparkles, Camera, Plus, Trash2, GripVertical,
  Save, Upload, Loader2, ImageIcon, X, ChevronDown, ChevronRight,
  Pencil, Search, Zap, Activity, CheckCircle2, XCircle, Copy, RefreshCw,
} from "lucide-react";

const TABS = [
  { id: "elements", label: "Elementos",                icon: ClipboardList },
  { id: "forms",    label: "Constructor de Formularios", icon: Sparkles },
  { id: "provisioning", label: "Aprovisionamiento",     icon: Zap },
  { id: "integrations", label: "Integraciones",         icon: Activity },
];

export default function FiscalizacionCenter() {
  const [tab, setTab] = useState("elements");

  return (
    <Layout
      title="Centro de Fiscalización"
      subtitle="Motor Matriz v9.7.0 · Elementos · Formularios"
    >
      <div className="mb-4 flex items-center gap-2" data-testid="fisc-center-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition ${
                active
                  ? "bg-[#0A3747] text-[#E0E621] shadow"
                  : "bg-white border border-gray-200 text-[#0A3747] hover:bg-gray-50"
              }`}
              data-testid={`fisc-tab-${t.id}`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "elements" && <ElementosTab />}
      {tab === "forms"    && <FormBuilderTab />}
      {tab === "provisioning" && <ProvisioningFlowTypesTab />}
      {tab === "integrations" && <IntegrationsCatalogTab />}
    </Layout>
  );
}


// ──────────────────────────────────────────────────────────────────
// TAB 2 · Constructor de Formularios
// ──────────────────────────────────────────────────────────────────
const FIELD_TYPES = [
  { id: "photo",     label: "📷 Cámara (Foto + IA)",   icon: "📷", accent: "#DC2626" },
  { id: "text",      label: "✏️  Texto",               icon: "✏️", accent: "#0EA5E9" },
  { id: "number",    label: "🔢 Número (Potencia)",   icon: "🔢", accent: "#F59E0B" },
  { id: "select",    label: "▾ Selector",             icon: "▾",  accent: "#7C3AED" },
  { id: "checkbox",  label: "☑ Checkbox",             icon: "☑",  accent: "#059669" },
  { id: "radio",     label: "◉ Opciones (Radio)",     icon: "◉",  accent: "#DB2777" },
  { id: "date",      label: "📅 Fecha",                icon: "📅", accent: "#0891B2" },
  { id: "textarea",  label: "📝 Texto largo",          icon: "📝", accent: "#6366F1" },
  { id: "signature", label: "✍️  Firma Digital",       icon: "✍️", accent: "#0A3747" },
  { id: "materials", label: "📦 Materiales (Receta)", icon: "📦", accent: "#B45309" },
  { id: "system_readonly", label: "🔒 Sistema (readonly)", icon: "🔒", accent: "#64748B" },
];

function FormBuilderTab() {
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ sections: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [criteriaList, setCriteriaList] = useState([]);
  // CRUD states
  const [showCreateType, setShowCreateType] = useState(false);
  const [showEditType, setShowEditType] = useState(null); // solution_type object
  const [newType, setNewType] = useState({ name: "", categoria: "", tipo_padre: "", tecnologia: "" });
  const [editTypeForm, setEditTypeForm] = useState({});
  const [crudSaving, setCrudSaving] = useState(false);
  const [flowTypes, setFlowTypes] = useState([]);
  // v96.0.0 · Buscador y árbol de formularios
  const [formQuery, setFormQuery] = useState("");
  const [openGroups, setOpenGroups] = useState({});

  // v37.0.0 · UID estable para campos (evita remount al editar key)
  const uidCounter = useRef(0);
  const nextUid = () => `_f${++uidCounter.current}_${Date.now()}`;
  const ensureUids = (formDef) => {
    if (!formDef?.sections) return formDef;
    return {
      ...formDef,
      sections: formDef.sections.map(sec => ({
        ...sec,
        fields: (sec.fields || []).map(f => f._uid ? f : { ...f, _uid: nextUid() }),
      })),
    };
  };

  const loadTypes = () => api.get("/v1/fisc-matrix/solution-types").then(r => setTypes(r.data.items || []));

  useEffect(() => {
    loadTypes();
    api.get("/v1/fisc-matrix/criteria-with-photos").then(r => setCriteriaList(r.data.items || []));
    api.get("/v1/fisc-matrix/provisioning-flow-types").then(r => setFlowTypes(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/v1/fisc-matrix/solution-types/${selected}/form`)
      .then(r => {
        const fd = r.data.form_definition || { sections: [] };
        if (!fd.sections) fd.sections = [];
        setForm(ensureUids(fd));
        setActiveSection(0);
        setDirty(false);
      })
      .catch(() => toast.error("No se pudo cargar el formulario"))
      .finally(() => setLoading(false));
  }, [selected]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/v1/fisc-matrix/solution-types/${selected}/form`, form);
      toast.success("Formulario guardado");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const autoSeed = async () => {
    if (!window.confirm("¿Regenerar los 38 formularios automáticamente desde la matriz?\n\nEsto SOBREESCRIBE los formularios existentes.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/v1/fisc-matrix/auto-seed-forms?overwrite=true");
      toast.success(`✅ ${data.created} formularios generados · ${data.total_fields} campos`);
      if (selected) {
        const r = await api.get(`/v1/fisc-matrix/solution-types/${selected}/form`);
        setForm(ensureUids(r.data.form_definition || { sections: [] }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "El auto-seed falló");
    } finally { setSeeding(false); }
  };

  const addSection = () => {
    setForm(p => ({ ...p, sections: [...(p.sections||[]), { title: "Nueva sección", fields: [] }] }));
    setDirty(true);
    setActiveSection((form.sections || []).length);
  };
  const rmSection = (i) => {
    setForm(p => ({ ...p, sections: p.sections.filter((_, idx) => idx !== i) }));
    setDirty(true);
    if (activeSection >= (form.sections?.length || 0) - 1) setActiveSection(Math.max(0, (form.sections?.length || 1) - 2));
  };
  const setSectionTitle = (i, v) => {
    setForm(p => {
      const s = [...p.sections]; s[i] = { ...s[i], title: v };
      return { ...p, sections: s };
    });
    setDirty(true);
  };

  // v4.0.0 · Añadir un campo desde la paleta a la sección activa
  const addFieldFromPalette = (fieldType) => {
    setForm(p => {
      const sections = [...(p.sections || [])];
      if (sections.length === 0) sections.push({ title: "Sección principal", fields: [] });
      const si = Math.min(activeSection, sections.length - 1);
      const defaults = {
        photo:     { required: true,  ai_enabled: true },
        text:      { required: false },
        number:    { required: false, min: null, max: null },
        select:    { required: false, options: ["Opción 1", "Opción 2"] },
        radio:     { required: false, options: ["Sí", "No"] },
        checkbox:  { required: false },
        date:      { required: false },
        textarea:  { required: false },
        signature: { required: true },
        materials: { required: true },
        system_readonly: { required: false },
      };
      const timestamp = Date.now();
      const newField = {
        _uid: nextUid(),
        key: `${fieldType.id}_${timestamp}`,
        label: fieldType.label.replace(/^[^\s]+\s+/, ""),   // sin el emoji
        type: fieldType.id,
        ...defaults[fieldType.id],
      };
      const flds = [...(sections[si].fields || []), newField];
      sections[si] = { ...sections[si], fields: flds };
      return { ...p, sections };
    });
    setDirty(true);
  };

  const rmField = (si, fi) => {
    setForm(p => {
      const s = [...p.sections];
      s[si] = { ...s[si], fields: s[si].fields.filter((_, idx) => idx !== fi) };
      return { ...p, sections: s };
    });
    setDirty(true);
  };
  const patchField = (si, fi, patch) => {
    setForm(p => {
      const s = [...p.sections];
      const flds = [...s[si].fields];
      flds[fi] = { ...flds[fi], ...patch };
      s[si] = { ...s[si], fields: flds };
      return { ...p, sections: s };
    });
    setDirty(true);
  };

  // v9.7.0 · Drag & Drop reorder (campos dentro de secciones)
  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === "FIELD") {
      const srcSecIdx = parseInt(source.droppableId.replace("section-", ""));
      const dstSecIdx = parseInt(destination.droppableId.replace("section-", ""));

      setForm(prev => {
        const sections = [...prev.sections];
        const srcFields = [...(sections[srcSecIdx].fields || [])];
        const [moved] = srcFields.splice(source.index, 1);

        if (srcSecIdx === dstSecIdx) {
          srcFields.splice(destination.index, 0, moved);
          sections[srcSecIdx] = { ...sections[srcSecIdx], fields: srcFields };
        } else {
          const dstFields = [...(sections[dstSecIdx].fields || [])];
          dstFields.splice(destination.index, 0, moved);
          sections[srcSecIdx] = { ...sections[srcSecIdx], fields: srcFields };
          sections[dstSecIdx] = { ...sections[dstSecIdx], fields: dstFields };
        }
        return { ...prev, sections };
      });
      setDirty(true);
    }

    if (type === "SECTION") {
      setForm(prev => {
        const sections = [...prev.sections];
        const [moved] = sections.splice(source.index, 1);
        sections.splice(destination.index, 0, moved);
        return { ...prev, sections };
      });
      setDirty(true);
      setActiveSection(destination.index);
    }
  };

  // v9.7.0 · CRUD de tipos de solución
  const createType = async () => {
    if (!newType.name.trim()) return toast.error("El nombre es obligatorio");
    setCrudSaving(true);
    try {
      const { data } = await api.post("/v1/fisc-matrix/solution-types", newType);
      toast.success(`Formulario "${data.solution_type.name}" creado`);
      setShowCreateType(false);
      setNewType({ name: "", categoria: "", tipo_padre: "", tecnologia: "" });
      await loadTypes();
      setSelected(data.solution_type.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al crear"); }
    finally { setCrudSaving(false); }
  };

  const startEditType = (t) => {
    setEditTypeForm({
      tipo_hija: t.tipo_hija || t.name || "",
      categoria: t.categoria || "",
      tipo_padre: t.tipo_padre || "",
      tecnologia: t.tecnologia || "",
      provisioning_flow_type_id: t.provisioning_flow_type_id || "",
    });
    setShowEditType(t);
  };

  const saveEditType = async () => {
    if (!showEditType) return;
    setCrudSaving(true);
    try {
      await api.patch(`/v1/fisc-matrix/solution-types/${showEditType.id}`, editTypeForm);
      toast.success("Tipo actualizado");
      setShowEditType(null);
      await loadTypes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al actualizar"); }
    finally { setCrudSaving(false); }
  };

  const deleteType = async (t) => {
    const msg = `¿Eliminar el formulario "${t.tipo_hija || t.name}"?\n\nSi tiene nodos de Cerebros vinculados, quedarán en Carril de Revisión.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/v1/fisc-matrix/solution-types/${t.id}?force=true`);
      const extra = data.orphaned_nodes > 0
        ? ` — ${data.orphaned_nodes} nodo(s) movidos a Carril de Revisión`
        : "";
      toast.success(`Formulario eliminado${extra}`);
      if (selected === t.id) { setSelected(null); setForm({ sections: [] }); }
      await loadTypes();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al eliminar"); }
  };

  // v96.0.0 · Árbol de jerarquía: Categoría → Tipo padre → Formularios (con búsqueda sin tildes)
  const _norm = (v) => (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const formTree = useMemo(() => {
    const nq = _norm(formQuery.trim());
    const tree = {};
    types.forEach(t => {
      const cat = (t.categoria || "Sin categoría").trim() || "Sin categoría";
      const pad = (t.tipo_padre || "Sin tipo").trim() || "Sin tipo";
      if (nq && ![t.tipo_hija, cat, pad].some(x => _norm(x).includes(nq))) return;
      tree[cat] = tree[cat] || {};
      tree[cat][pad] = tree[cat][pad] || [];
      tree[cat][pad].push(t);
    });
    return tree;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types, formQuery]);
  const toggleGroup = (k) => setOpenGroups(o => ({ ...o, [k]: !o[k] }));

  // v96.0.0 · Duplicar formulario (ficha + definición + reglas de fiscalización)
  const duplicateType = async (t) => {
    const name = window.prompt("Nombre del nuevo formulario", `${t.tipo_hija} (copia)`);
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post(`/v1/fisc-matrix/solution-types/${t.id}/duplicate`, { name: name.trim() });
      toast.success(`Formulario duplicado: ${data.name}`);
      await loadTypes();
      setSelected(data.id);
      setFormQuery("");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo duplicar el formulario"); }
  };

  const currentType = types.find(t => t.id === selected);

  return (
    <div className="space-y-3" data-testid="fisc-tab-content-forms">
      {/* ══════ v96.0.0 · Elementos (paleta de campos) como barra superior ══════ */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-3 py-2 flex flex-wrap items-center gap-1.5" data-testid="fisc-form-palette">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-1">🎨 Elementos</span>
        {FIELD_TYPES.map(ft => (
          <button
            key={ft.id}
            onClick={() => addFieldFromPalette(ft)}
            disabled={!selected}
            className="px-2 py-1 rounded border border-gray-200 text-[11px] font-semibold hover:border-[#0A3747] hover:shadow disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ borderLeftWidth: 4, borderLeftColor: ft.accent }}
            data-testid={`fisc-palette-${ft.id}`}
          >
            {ft.label}
          </button>
        ))}
        {!selected && <span className="text-[10px] italic text-slate-400 ml-1">Selecciona un formulario para añadir campos a la sección activa</span>}
      </div>

    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_300px] gap-3">
      {/* ══════ Columna 1 · Formularios agrupados por jerarquía ══════ */}
      <aside className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col max-h-[78vh] min-h-[420px]">
        <header className="px-3 py-2 bg-slate-50 border-b border-gray-200 text-[11px] font-bold text-slate-600 flex items-center gap-1.5">
          <span className="flex-1">Formularios ({types.length})</span>
          <button
            onClick={() => setShowCreateType(true)}
            className="px-2 py-0.5 rounded bg-[#0A3747] text-[#E0E621] text-[9px] font-bold flex items-center gap-1 hover:bg-[#0c4a5f]"
            data-testid="fisc-form-create-type" title="Crear nuevo formulario"
          ><Plus size={9} /> Nuevo</button>
          <button
            onClick={() => currentType && duplicateType(currentType)}
            disabled={!currentType}
            className="px-2 py-0.5 rounded bg-sky-600 text-white text-[9px] font-bold flex items-center gap-1 disabled:opacity-40"
            data-testid="fisc-form-duplicate" title="Duplicar el formulario seleccionado"
          ><Copy size={9} /> Duplicar</button>
          <button
            onClick={autoSeed}
            disabled={seeding}
            className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[9px] font-bold flex items-center gap-1 disabled:opacity-60"
            data-testid="fisc-form-autoseed" title="Regenerar los formularios desde la matriz"
          >
            {seeding ? <Loader2 size={9} className="animate-spin" /> : "S"} SEED
          </button>
        </header>
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={formQuery} onChange={e => setFormQuery(e.target.value)}
              placeholder="Buscar formulario, categoría o tipo…"
              className="w-full pl-7 pr-2 py-1.5 text-[11px] rounded-lg border border-gray-200"
              data-testid="fisc-form-search" />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-1" data-testid="fisc-form-tree">
          {Object.keys(formTree).length === 0 && (
            <div className="text-[11px] text-slate-400 text-center py-6">Sin formularios que coincidan</div>
          )}
          {Object.entries(formTree).sort(([a], [b]) => a.localeCompare(b)).map(([cat, padres]) => {
            const catOpen = !!formQuery.trim() || !!openGroups[cat];
            const nCat = Object.values(padres).reduce((acc, l) => acc + l.length, 0);
            return (
              <div key={cat}>
                <button onClick={() => toggleGroup(cat)}
                  className="w-full flex items-center gap-1 px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider text-[#0A3747] hover:bg-slate-50 rounded"
                  data-testid={`fisc-form-group-${cat}`}>
                  {catOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  <span className="flex-1 truncate">{cat}</span>
                  <span className="text-[9px] text-slate-400 font-normal">{nCat}</span>
                </button>
                {catOpen && Object.entries(padres).sort(([a], [b]) => a.localeCompare(b)).map(([pad, list]) => {
                  const key = `${cat}::${pad}`;
                  const padOpen = !!formQuery.trim() || !!openGroups[key];
                  return (
                    <div key={key}>
                      <button onClick={() => toggleGroup(key)}
                        className="w-full flex items-center gap-1 pl-5 pr-2 py-1 text-left text-[10px] font-semibold text-slate-600 hover:bg-slate-50 rounded">
                        {padOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                        <span className="flex-1 truncate">{pad}</span>
                        <span className="text-[9px] text-slate-400 font-normal">{list.length}</span>
                      </button>
                      {padOpen && list.map(t => (
                        <div key={t.id}
                          className={`flex items-center rounded hover:bg-yellow-50 group ${selected === t.id ? "bg-yellow-100" : ""}`}>
                          <button
                            onClick={() => setSelected(t.id)}
                            className={`flex-1 text-left pl-9 pr-2 py-1.5 text-[10px] ${selected === t.id ? "font-bold text-[#0A3747]" : "text-slate-700"}`}
                            data-testid={`fisc-form-type-${t.id}`}
                          >{t.tipo_hija}</button>
                          <div className="flex items-center pr-1 gap-0.5 opacity-0 group-hover:opacity-100 transition">
                            <button onClick={() => duplicateType(t)} className="p-1 text-slate-400 hover:text-sky-600" title="Duplicar" data-testid={`fisc-form-dup-type-${t.id}`}><Copy size={10} /></button>
                            <button onClick={() => startEditType(t)} className="p-1 text-slate-400 hover:text-blue-600" title="Editar" data-testid={`fisc-form-edit-type-${t.id}`}><Pencil size={10} /></button>
                            <button onClick={() => deleteType(t)} className="p-1 text-slate-400 hover:text-red-600" title="Eliminar" data-testid={`fisc-form-del-type-${t.id}`}><Trash2 size={10} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </aside>

      {/* ══════ Columna 2 · Lienzo del constructor con DnD ══════ */}
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 min-h-[400px]">
        {/* Modal: Crear tipo */}
        {showCreateType && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="fisc-form-create-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
              <div className="text-sm font-bold text-[#0A3747]">Nuevo Formulario</div>
              <div className="space-y-3">
                <label className="block"><div className="text-[10px] font-bold text-slate-500 mb-1">Nombre (tipo_hija) *</div>
                  <input value={newType.name} onChange={e => setNewType(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" placeholder="Ej: INSTALACIÓN OLT HUAWEI" data-testid="fisc-form-create-name" /></label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><div className="text-[10px] font-bold text-slate-500 mb-1">Categoria</div>
                    <input value={newType.categoria} onChange={e => setNewType(p => ({ ...p, categoria: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" placeholder="COBRE/FIBRA" /></label>
                  <label className="block"><div className="text-[10px] font-bold text-slate-500 mb-1">Tipo Padre</div>
                    <input value={newType.tipo_padre} onChange={e => setNewType(p => ({ ...p, tipo_padre: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" placeholder="INSTALACIÓN" /></label>
                </div>
                <label className="block"><div className="text-[10px] font-bold text-slate-500 mb-1">Tecnologia</div>
                  <input value={newType.tecnologia} onChange={e => setNewType(p => ({ ...p, tecnologia: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" placeholder="GPON/VDSL" /></label>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowCreateType(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-xs font-bold text-slate-600 hover:bg-gray-50">Cancelar</button>
                <button onClick={createType} disabled={crudSaving} className="px-4 py-2 rounded-lg bg-[#0A3747] text-[#E0E621] text-xs font-bold disabled:opacity-60 flex items-center gap-1.5" data-testid="fisc-form-create-submit">
                  {crudSaving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Crear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Editar tipo */}
        {showEditType && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="fisc-form-edit-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
              <div className="text-sm font-bold text-[#0A3747]">Editar Formulario</div>
              <div className="space-y-3">
                <label className="block"><div className="text-[10px] font-bold text-slate-500 mb-1">Nombre</div>
                  <input value={editTypeForm.tipo_hija || ""} onChange={e => setEditTypeForm(p => ({ ...p, tipo_hija: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" data-testid="fisc-form-edit-name" /></label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><div className="text-[10px] font-bold text-slate-500 mb-1">Categoria</div>
                    <input value={editTypeForm.categoria || ""} onChange={e => setEditTypeForm(p => ({ ...p, categoria: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" /></label>
                  <label className="block"><div className="text-[10px] font-bold text-slate-500 mb-1">Tipo Padre</div>
                    <input value={editTypeForm.tipo_padre || ""} onChange={e => setEditTypeForm(p => ({ ...p, tipo_padre: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" /></label>
                </div>
                <label className="block"><div className="text-[10px] font-bold text-slate-500 mb-1">Tecnologia</div>
                  <input value={editTypeForm.tecnologia || ""} onChange={e => setEditTypeForm(p => ({ ...p, tecnologia: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" /></label>
                <div className="flex items-center justify-between py-2 px-1" data-testid="fisc-form-edit-cpe-toggle">
                  <div>
                    <div className="text-[10px] font-bold text-slate-500">Tipo de Aprovisionamiento</div>
                    <div className="text-[9px] text-slate-400">Define el flujo de provisioning para este tipo de solución</div>
                  </div>
                  <select
                    value={editTypeForm.provisioning_flow_type_id || ""}
                    onChange={e => setEditTypeForm(p => ({ ...p, provisioning_flow_type_id: e.target.value || null }))}
                    className="border border-gray-300 rounded-lg text-xs px-3 py-1.5 bg-white min-w-[160px]"
                    data-testid="fisc-form-edit-flow-type"
                  >
                    <option value="">Sin aprovisionamiento</option>
                    {flowTypes.filter(ft => ft.active).map(ft => (
                      <option key={ft.id} value={ft.id}>{ft.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowEditType(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-xs font-bold text-slate-600 hover:bg-gray-50">Cancelar</button>
                <button onClick={saveEditType} disabled={crudSaving} className="px-4 py-2 rounded-lg bg-[#0A3747] text-[#E0E621] text-xs font-bold disabled:opacity-60 flex items-center gap-1.5" data-testid="fisc-form-edit-submit">
                  {crudSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {!selected && (
          <div className="text-center text-gray-400 italic py-16">
            Selecciona un tipo de solución para construir su formulario
          </div>
        )}
        {selected && loading && (
          <div className="text-center py-12 text-gray-500"><Loader2 size={16} className="animate-spin inline mr-2" /> Cargando…</div>
        )}
        {selected && !loading && (
          <>
            <header className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
              <div className="flex-1">
                <div className="text-sm font-bold text-[#0A3747]">{currentType?.tipo_hija}</div>
                <div className="text-[11px] text-gray-500">
                  {form.auto_generated ? `Auto-generado ${form.version || ""}` : "Formulario personalizado"}
                  {dirty && <span className="ml-2 text-amber-600 font-bold">Cambios sin guardar</span>}
                </div>
              </div>
              <button
                onClick={addSection}
                className="px-3 py-1.5 rounded bg-white border border-gray-300 text-[#0A3747] text-xs font-bold flex items-center gap-1.5 hover:bg-gray-50"
                data-testid="fisc-form-add-section"
              >
                <Plus size={12} /> Sección
              </button>
              <button
                onClick={save}
                disabled={saving || !dirty}
                className="px-3 py-1.5 rounded bg-[#0A3747] text-[#E0E621] text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
                data-testid="fisc-form-save"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                {dirty ? "Guardar" : "Guardado"}
              </button>
            </header>

            {(form.sections || []).length === 0 && (
              <div className="text-center py-8 text-gray-400 italic text-xs">
                Aún no hay secciones. Toca <b>Sección</b> para comenzar.
              </div>
            )}

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sections-list" type="SECTION">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
                    {(form.sections || []).map((sec, si) => {
                      const isActive = si === activeSection;
                      return (
                        <Draggable key={`section-${si}`} draggableId={`section-${si}`} index={si}>
                          {(dragProvided, dragSnapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              onClick={() => setActiveSection(si)}
                              className={`border rounded-lg overflow-hidden cursor-pointer transition-shadow ${isActive ? "border-[#0A3747] shadow-lg ring-2 ring-[#E0E621]/40" : "border-gray-200"} ${dragSnapshot.isDragging ? "shadow-2xl opacity-90" : ""}`}
                              data-testid={`fisc-form-section-${si}`}
                            >
                              <div className="bg-slate-50 px-3 py-2 flex items-center gap-2 border-b border-gray-200">
                                <div {...dragProvided.dragHandleProps} className="cursor-grab active:cursor-grabbing" title="Arrastrar sección">
                                  <GripVertical size={13} className="text-gray-400" />
                                </div>
                                <input
                                  value={sec.title || ""}
                                  onChange={e => setSectionTitle(si, e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  className="flex-1 bg-transparent text-sm font-bold text-[#0A3747] focus:outline-none"
                                  placeholder="Título de la sección"
                                  data-testid={`fisc-form-section-title-${si}`}
                                />
                                {isActive && <span className="text-[9px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full">ACTIVA</span>}
                                <button
                                  onClick={(e) => { e.stopPropagation(); rmSection(si); }}
                                  className="text-red-500 hover:text-red-700"
                                  data-testid={`fisc-form-rm-section-${si}`}
                                ><Trash2 size={13} /></button>
                              </div>

                              <Droppable droppableId={`section-${si}`} type="FIELD">
                                {(fieldProvided, fieldSnapshot) => (
                                  <div
                                    ref={fieldProvided.innerRef}
                                    {...fieldProvided.droppableProps}
                                    className={`p-3 space-y-2 min-h-[40px] transition-colors ${fieldSnapshot.isDraggingOver ? "bg-yellow-50" : ""}`}
                                    onClick={e => e.stopPropagation()}
                                  >
                                    {(sec.fields || []).length === 0 && !fieldSnapshot.isDraggingOver && (
                                      <div className="text-[11px] text-gray-400 italic py-2 text-center">
                                        Arrastra campos aquí o usa la paleta
                                      </div>
                                    )}
                                    {(sec.fields || []).map((f, fi) => (
                                      <Draggable key={f._uid} draggableId={f._uid} index={fi}>
                                        {(fDragProv, fDragSnap) => (
                                          <div ref={fDragProv.innerRef} {...fDragProv.draggableProps}>
                                            <FieldRow
                                              field={f}
                                              onChange={patch => patchField(si, fi, patch)}
                                              onRemove={() => rmField(si, fi)}
                                              testid={`fisc-form-field-${si}-${fi}`}
                                              criteriaList={criteriaList}
                                              dragHandleProps={fDragProv.dragHandleProps}
                                              isDragging={fDragSnap.isDragging}
                                            />
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                    {fieldProvided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </section>

      {/* ══════ Columna 3 · Preview móvil ══════ */}
      <aside className="hidden lg:block sticky top-4" style={{ height: "fit-content" }} data-testid="fisc-form-preview">
        <MobilePreview form={form} typeName={currentType?.tipo_hija} />
      </aside>
    </div>
    </div>
  );
}


// ────────── FIELD ROW · editor inline por campo ──────────
function FieldRow({ field, onChange, onRemove, testid, criteriaList, dragHandleProps, isDragging }) {
  const [expanded, setExpanded] = useState(false);
  const [criteriaSearch, setCriteriaSearch] = useState("");
  const [showCriteriaDropdown, setShowCriteriaDropdown] = useState(false);
  const criteriaAnchorRef = useRef(null);   // v101.98.0
  const F = field;
  const accent = FIELD_TYPES.find(t => t.id === F.type)?.accent || "#64748B";

  const filteredCriteria = useMemo(() => {
    if (!criteriaSearch.trim()) return criteriaList || [];
    const q = criteriaSearch.toLowerCase();
    return (criteriaList || []).filter(c => c.name.toLowerCase().includes(q) || String(c.id).includes(q));
  }, [criteriaList, criteriaSearch]);

  const selectedCriteria = (criteriaList || []).find(c => c.id === F.criteria_id);

  return (
    <div className={`rounded border border-gray-200 bg-slate-50 transition-shadow ${isDragging ? "shadow-xl ring-2 ring-[#E0E621]/50 opacity-90" : ""}`} data-testid={testid} style={{ borderLeftWidth: 3, borderLeftColor: accent }}>
      <div className="grid grid-cols-[20px_110px_1fr_120px_90px_28px_28px] gap-2 items-center p-2">
        <div {...(dragHandleProps || {})} className="cursor-grab active:cursor-grabbing flex items-center justify-center" title="Arrastrar para reordenar">
          <GripVertical size={12} className="text-gray-400" />
        </div>
        <input
          value={F.key} onChange={e => onChange({ key: e.target.value.replace(/[^\w]/g, "_").toLowerCase() })}
          className="px-2 py-1 rounded border border-gray-300 text-[10px] font-mono"
          placeholder="clave"
        />
        <input
          value={F.label} onChange={e => onChange({ label: e.target.value })}
          className="px-2 py-1 rounded border border-gray-300 text-[11px]"
          placeholder="Etiqueta visible"
        />
        <select
          value={F.type} onChange={e => onChange({ type: e.target.value })}
          className="px-2 py-1 rounded border border-gray-300 text-[10px] bg-white"
        >
          {FIELD_TYPES.map(ft => <option key={ft.id} value={ft.id}>{ft.label}</option>)}
        </select>
        <label className="text-[10px] text-gray-600 flex items-center gap-1">
          <input type="checkbox" checked={!!F.required} onChange={e => onChange({ required: e.target.checked })} /> Obligat.
        </label>
        <button onClick={() => setExpanded(v => !v)} className="text-blue-600 hover:text-blue-800 justify-self-center" title="Configurar">
          {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        <button onClick={onRemove} className="text-red-500 hover:text-red-700 justify-self-center"><Trash2 size={12} /></button>
      </div>
      {expanded && (
        <div className="border-t border-gray-200 bg-white p-3 space-y-2 text-[11px]">
          {(F.type === "select" || F.type === "radio") && (
            <label className="block">
              <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1">Opciones (coma-separadas)</div>
              <input
                value={(F.options || []).join(", ")}
                onChange={e => onChange({ options: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })}
                className="w-full px-2 py-1 rounded border border-gray-300"
                placeholder="SI, NO, PENDIENTE"
              />
            </label>
          )}
          {F.type === "number" && (
            <div className="grid grid-cols-3 gap-2">
              <label><div className="text-[9px] font-bold text-slate-500">Mín</div>
                <input type="number" value={F.min ?? ""} onChange={e => onChange({ min: e.target.value === "" ? null : parseFloat(e.target.value) })} className="w-full px-2 py-1 rounded border border-gray-300" /></label>
              <label><div className="text-[9px] font-bold text-slate-500">Máx</div>
                <input type="number" value={F.max ?? ""} onChange={e => onChange({ max: e.target.value === "" ? null : parseFloat(e.target.value) })} className="w-full px-2 py-1 rounded border border-gray-300" /></label>
              <label><div className="text-[9px] font-bold text-slate-500">Paso</div>
                <input type="number" value={F.step ?? ""} onChange={e => onChange({ step: e.target.value === "" ? null : parseFloat(e.target.value) })} className="w-full px-2 py-1 rounded border border-gray-300" /></label>
            </div>
          )}
          {F.type === "photo" && (
            <>
              {/* v9.7.0 · Selector de Elemento (reemplaza input manual de rule_id) */}
              <div className="relative" data-testid="fisc-form-element-selector">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1">Elemento (Criterio IA)</div>
                {selectedCriteria ? (
                  <div className="flex items-center gap-2 p-2 rounded-lg border border-emerald-200 bg-emerald-50">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold text-[#0A3747] flex items-center gap-1.5">
                        <span className="bg-emerald-600 text-white text-[8px] font-bold rounded px-1 py-0.5">#{selectedCriteria.id}</span>
                        {selectedCriteria.name}
                      </div>
                      {selectedCriteria.que_debe_validar && (
                        <div className="text-[9px] text-emerald-700 truncate mt-0.5">{selectedCriteria.que_debe_validar}</div>
                      )}
                    </div>
                    <button onClick={() => { onChange({ criteria_id: null, rule_id: null }); setCriteriaSearch(""); }}
                      className="text-red-500 hover:text-red-700 shrink-0" title="Desvincular"><X size={12} /></button>
                  </div>
                ) : (
                  <div className="relative" ref={criteriaAnchorRef}>
                    <input
                      value={criteriaSearch}
                      onChange={e => { setCriteriaSearch(e.target.value); setShowCriteriaDropdown(true); }}
                      onFocus={() => setShowCriteriaDropdown(true)}
                      className="w-full px-2 py-1.5 rounded border border-gray-300 text-[11px]"
                      placeholder="Buscar elemento por nombre o #ID..."
                      data-testid="fisc-form-element-search"
                    />
                    {showCriteriaDropdown && (
                      <FloatingPanel anchorRef={criteriaAnchorRef} matchWidth maxHeight={192} className="bg-white border border-gray-200 rounded-lg shadow-xl">
                        {filteredCriteria.length === 0 && (
                          <div className="px-3 py-2 text-[10px] text-slate-400 italic">Sin resultados</div>
                        )}
                        {filteredCriteria.map(c => (
                          <button
                            key={c.id}
                            onClick={() => {
                              onChange({ criteria_id: c.id, rule_id: null });
                              setCriteriaSearch("");
                              setShowCriteriaDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-yellow-50 border-b border-gray-50 flex items-center gap-2"
                            data-testid={`fisc-form-element-opt-${c.id}`}
                          >
                            <span className="bg-slate-200 text-slate-600 text-[8px] font-bold rounded px-1 py-0.5 shrink-0">#{c.id}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] font-bold text-[#0A3747] truncate">{c.name}</div>
                              {c.que_debe_validar && <div className="text-[9px] text-slate-400 truncate">{c.que_debe_validar}</div>}
                            </div>
                            {c.photo_count > 0 && <span className="text-[8px] bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">{c.photo_count} foto</span>}
                          </button>
                        ))}
                      </FloatingPanel>
                    )}
                  </div>
                )}
                <div className="text-[9px] text-slate-500 mt-1">
                  Al vincular un elemento, la app usa <code className="bg-slate-100 px-1">POST /fisc-matrix/validate-photo</code> con sus fotos de referencia y reglas IA.
                </div>
              </div>
              <label className="block">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1">Pista para el técnico</div>
                <input
                  value={F.hint || ""} onChange={e => onChange({ hint: e.target.value })}
                  className="w-full px-2 py-1 rounded border border-gray-300"
                  placeholder="ej. Foco en la etiqueta de la roseta"
                />
              </label>
              <label className="block">
                <input type="checkbox" checked={!!F.ai_enabled} onChange={e => onChange({ ai_enabled: e.target.checked })} />
                <span className="ml-2 text-[10px]">Validación IA activa (Nano Banana)</span>
              </label>
            </>
          )}
          {(F.type === "text" || F.type === "textarea") && (
            <label className="block">
              <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500 mb-1">Placeholder</div>
              <input value={F.placeholder || ""} onChange={e => onChange({ placeholder: e.target.value })} className="w-full px-2 py-1 rounded border border-gray-300" />
            </label>
          )}
        </div>
      )}
    </div>
  );
}


// ────────── MOBILE PREVIEW · simulador de celular ──────────
function MobilePreview({ form, typeName }) {
  return (
    <div className="w-full">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 text-center">
        📱 Vista Previa Móvil
      </div>
      <div className="mx-auto rounded-[36px] p-2 shadow-2xl" style={{ background: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)", width: 260 }}>
        <div className="rounded-[28px] bg-white overflow-hidden" style={{ minHeight: 460, maxHeight: 600 }}>
          {/* Notch */}
          <div className="h-6 bg-slate-900 flex items-center justify-center relative">
            <div className="w-16 h-3 bg-black rounded-b-lg" />
          </div>
          {/* App header · navy */}
          <div className="bg-[#0A3747] text-white px-3 py-2">
            <div className="text-[9px] text-[#E0E621] font-bold">FISCALIZACIÓN</div>
            <div className="text-[11px] font-bold truncate">{typeName || "—"}</div>
          </div>
          {/* Body scrollable */}
          <div className="overflow-y-auto p-2 space-y-3" style={{ maxHeight: 420 }}>
            {(form.sections || []).length === 0 && (
              <div className="text-center text-[9px] text-gray-400 italic pt-10">Sin campos aún</div>
            )}
            {(form.sections || []).map((sec, i) => (
              <div key={i} className="space-y-1.5">
                <div className="text-[8px] font-black tracking-widest text-[#0A3747] uppercase border-b border-[#E0E621] pb-1">
                  {sec.title}
                </div>
                {(sec.fields || []).map((f, j) => <PreviewField key={j} field={f} />)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewField({ field: f }) {
  const req = f.required ? <span className="text-red-500">*</span> : null;
  if (f.type === "photo") return (
    <div className="rounded border-2 border-dashed border-[#DC2626] p-2 bg-red-50">
      <div className="text-[9px] font-bold text-[#0A3747] mb-1">📷 {f.label} {req}</div>
      <div className="h-14 bg-white rounded flex items-center justify-center text-[9px] text-gray-400">Cámara</div>
      {f.ai_enabled && <div className="text-[8px] text-red-600 mt-1 font-mono">🤖 IA · elem #{f.criteria_id || f.rule_id || "?"}</div>}
    </div>
  );
  if (f.type === "signature") return (
    <div className="rounded bg-[#0A3747]/5 p-2">
      <div className="text-[9px] font-bold text-[#0A3747] mb-1">✍️ {f.label} {req}</div>
      <div className="h-10 bg-white rounded border border-gray-300 flex items-center justify-center text-[9px] text-gray-400">Firma aquí</div>
    </div>
  );
  if (f.type === "materials") return (
    <div className="rounded bg-amber-50 p-2 border border-amber-200">
      <div className="text-[9px] font-bold text-amber-900 mb-1">📦 {f.label} {req}</div>
      <div className="text-[8px] text-amber-800">Añadir de la receta →</div>
    </div>
  );
  if (f.type === "checkbox") return (
    <label className="flex items-center gap-1.5 text-[10px] text-[#0A3747]">
      <input type="checkbox" className="w-3 h-3" disabled /> {f.label} {req}
    </label>
  );
  if (f.type === "number") return (
    <div><div className="text-[9px] font-bold text-[#0A3747] mb-0.5">🔢 {f.label} {req}</div>
      <div className="h-6 bg-slate-50 border border-slate-200 rounded px-1 text-[9px] flex items-center text-gray-400">0.0</div>
    </div>
  );
  if (f.type === "select" || f.type === "radio") return (
    <div><div className="text-[9px] font-bold text-[#0A3747] mb-0.5">▾ {f.label} {req}</div>
      <div className="h-6 bg-slate-50 border border-slate-200 rounded px-1 text-[9px] flex items-center text-gray-400">Seleccionar…</div>
    </div>
  );
  if (f.type === "textarea") return (
    <div><div className="text-[9px] font-bold text-[#0A3747] mb-0.5">📝 {f.label} {req}</div>
      <div className="h-10 bg-slate-50 border border-slate-200 rounded" /></div>
  );
  if (f.type === "system_readonly") return (
    <div className="rounded bg-slate-100 p-1.5">
      <div className="text-[8px] font-bold text-slate-500">🔒 {f.label} (auto)</div>
    </div>
  );
  // text/date/default
  return (
    <div><div className="text-[9px] font-bold text-[#0A3747] mb-0.5">{f.label} {req}</div>
      <div className="h-6 bg-slate-50 border border-slate-200 rounded" /></div>
  );
}


// ──────────────────────────────────────────────────────────────────
// TAB 1 · Elementos (v9.7.0 — basado en fisc_matrix_criteria)
// ──────────────────────────────────────────────────────────────────
function ElementosTab() {
  const [criteria, setCriteria] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [signedMap, setSignedMap] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCrit, setNewCrit] = useState({ name: "", que_debe_validar: "" });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const inputRefs = useRef({});

  const refreshSignedUrls = useCallback(async (items) => {
    const allPaths = items.flatMap(c => (c.reference_photo_urls || []).filter(Boolean));
    if (!allPaths.length) return;
    const signed = await getSignedUrls(allPaths);
    setSignedMap(prev => ({ ...prev, ...signed }));
  }, []);

  const loadCriteria = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/fisc-matrix/criteria-with-photos");
      const items = r.data.items || [];
      setCriteria(items);
      await refreshSignedUrls(items);
    } catch { toast.error("No se pudieron cargar los elementos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCriteria(); }, []);

  const loadDetail = async (cid) => {
    setSelected(cid);
    setEditing(false);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/v1/fisc-matrix/criteria/${cid}`);
      setDetail(data);
      const urls = (data.reference_photo_urls || []).filter(Boolean);
      if (urls.length) {
        const signed = await getSignedUrls(urls);
        setSignedMap(prev => ({ ...prev, ...signed }));
      }
    } catch { toast.error("No se pudo cargar el detalle"); }
    finally { setDetailLoading(false); }
  };

  const createCriteria = async () => {
    if (!newCrit.name.trim()) return toast.error("El nombre es obligatorio");
    setCreating(true);
    try {
      const { data } = await api.post("/v1/fisc-matrix/criteria", newCrit);
      toast.success(`Elemento #${data.criteria.id} creado`);
      setShowCreate(false);
      setNewCrit({ name: "", que_debe_validar: "" });
      await loadCriteria();
      loadDetail(data.criteria.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al crear"); }
    finally { setCreating(false); }
  };

  const deleteCriteria = async (cid) => {
    const c = criteria.find(x => x.id === cid);
    const msg = c?.solution_count > 0
      ? `El elemento "${c.name}" está siendo usado por ${c.solution_count} tipo(s) de solución. ¿Desactivar de todas formas?`
      : `¿Desactivar el elemento "${c?.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/v1/fisc-matrix/criteria/${cid}?force=true`);
      toast.success("Elemento desactivado");
      if (selected === cid) { setSelected(null); setDetail(null); }
      await loadCriteria();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al eliminar"); }
  };

  const startEdit = () => {
    if (!detail) return;
    setEditForm({
      name: detail.name || "",
      que_debe_validar: detail.que_debe_validar || "",
      parametro_umbral: detail.parametro_umbral || "",
      si_cumple: detail.si_cumple || "",
      si_no_cumple: detail.si_no_cumple || "",
      si_no_verificable: detail.si_no_verificable || "",
      excepciones: detail.excepciones || "",
      dependencia: detail.dependencia || "",
      reintentos_default: detail.reintentos_default ?? 3,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/v1/fisc-matrix/criteria/${selected}`, editForm);
      toast.success("Elemento actualizado");
      setEditing(false);
      await loadCriteria();
      await loadDetail(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (cid, slot, file) => {
    if (!file) return;
    setUploading(`${cid}-${slot}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("slot", String(slot));
      const { data } = await api.post(`/v1/fisc-matrix/criteria/${cid}/reference-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newUrls = data.reference_photo_urls || [];
      setCriteria(prev => prev.map(c => c.id === cid ? { ...c, reference_photo_urls: newUrls, photo_count: newUrls.filter(Boolean).length } : c));
      if (detail && detail.id === cid) setDetail(prev => ({ ...prev, reference_photo_urls: newUrls }));
      const newPaths = newUrls.filter(Boolean);
      if (newPaths.length) { const signed = await getSignedUrls(newPaths); setSignedMap(prev => ({ ...prev, ...signed })); }
      toast.success(data.is_shared ? `Foto subida. Aplica a ${data.affected_solutions} tipos.` : "Foto subida");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al subir foto"); }
    finally { setUploading(null); }
  };

  const deletePhoto = async (cid, slot) => {
    if (!window.confirm("¿Quitar esta foto? Afectará a TODOS los tipos de solución que usen este criterio.")) return;
    try {
      await api.delete(`/v1/fisc-matrix/criteria/${cid}/reference-photo?slot=${slot}`);
      setCriteria(prev => prev.map(c => {
        if (c.id !== cid) return c;
        const urls = [...(c.reference_photo_urls || [])];
        if (slot < urls.length) urls[slot] = null;
        while (urls.length && !urls[urls.length - 1]) urls.pop();
        return { ...c, reference_photo_urls: urls, photo_count: urls.filter(Boolean).length };
      }));
      if (detail && detail.id === cid) {
        setDetail(prev => {
          const urls = [...(prev.reference_photo_urls || [])];
          if (slot < urls.length) urls[slot] = null;
          while (urls.length && !urls[urls.length - 1]) urls.pop();
          return { ...prev, reference_photo_urls: urls };
        });
      }
      toast.success("Foto eliminada");
    } catch { toast.error("No se pudo eliminar"); }
  };

  const getSlotUrl = (urls, slot) => {
    const raw = urls?.[slot] || null;
    return raw ? (signedMap[raw] || raw) : null;
  };

  const filtered = useMemo(() => {
    if (filter === "with_photos") return criteria.filter(c => c.photo_count > 0);
    if (filter === "shared") return criteria.filter(c => c.is_shared);
    return criteria;
  }, [criteria, filter]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4" data-testid="fisc-tab-content-elements">
      {/* ═══ Sidebar: lista de elementos ═══ */}
      <aside className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col max-h-[80vh]">
        <header className="px-3 py-2.5 bg-slate-50 border-b border-gray-200 flex items-center gap-2">
          <ClipboardList size={14} className="text-[#0A3747]" />
          <span className="text-[11px] font-bold text-[#0A3747] flex-1">Elementos ({criteria.length})</span>
          <button
            onClick={() => setShowCreate(true)}
            className="px-2 py-1 rounded-lg bg-[#0A3747] text-[#E0E621] text-[9px] font-bold flex items-center gap-1 hover:bg-[#0c4a5f]"
            data-testid="fisc-elem-create-btn"
          ><Plus size={10} /> Nuevo</button>
        </header>

        {/* Filtros inline */}
        <div className="px-2 py-1.5 flex gap-1 border-b border-gray-100 bg-white">
          {[
            { id: "all", label: "Todos" },
            { id: "with_photos", label: "Con fotos" },
            { id: "shared", label: "Compartidos" },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-2 py-0.5 rounded text-[9px] font-bold transition ${
                filter === f.id ? "bg-[#0A3747] text-[#E0E621]" : "text-slate-500 hover:bg-gray-100"
              }`}
              data-testid={`fisc-elem-filter-${f.id}`}
            >{f.label}</button>
          ))}
        </div>

        {/* Lista */}
        <div className="overflow-y-auto flex-1">
          {loading && <div className="text-center py-8"><Loader2 size={14} className="animate-spin inline" /></div>}
          {!loading && filtered.map(c => (
            <button
              key={c.id}
              onClick={() => loadDetail(c.id)}
              className={`w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-yellow-50 flex items-center gap-2 transition ${
                selected === c.id ? "bg-yellow-50 border-l-4 border-l-[#E0E621]" : ""
              }`}
              data-testid={`fisc-elem-item-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold text-[#0A3747] truncate flex items-center gap-1.5">
                  <span className="bg-slate-200 text-slate-600 text-[8px] font-bold rounded px-1 py-0.5 shrink-0">#{c.id}</span>
                  {c.name}
                </div>
                {c.que_debe_validar && <div className="text-[9px] text-slate-400 truncate mt-0.5">{c.que_debe_validar}</div>}
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                {c.photo_count > 0 && <span className="text-[8px] font-bold bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">{c.photo_count} foto{c.photo_count > 1 ? "s" : ""}</span>}
                {c.is_shared && <span className="text-[8px] font-bold bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">{c.solution_count} sol.</span>}
              </div>
            </button>
          ))}
          {!loading && !filtered.length && <div className="text-center py-8 text-[10px] text-slate-400 italic">Sin resultados</div>}
        </div>
      </aside>

      {/* ═══ Panel principal: detalle del elemento ═══ */}
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden min-h-[400px]">
        {/* Modal crear nuevo */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="fisc-elem-create-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-4">
              <div className="text-sm font-bold text-[#0A3747]">Nuevo Elemento (Criterio)</div>
              <div className="space-y-3">
                <label className="block">
                  <div className="text-[10px] font-bold text-slate-500 mb-1">Nombre *</div>
                  <input value={newCrit.name} onChange={e => setNewCrit(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" placeholder="Ej: Validar Etiqueta ONT" data-testid="fisc-elem-create-name" />
                </label>
                <label className="block">
                  <div className="text-[10px] font-bold text-slate-500 mb-1">Qué debe validar</div>
                  <textarea value={newCrit.que_debe_validar} onChange={e => setNewCrit(p => ({ ...p, que_debe_validar: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" rows={3} placeholder="Descripción de lo que se valida" data-testid="fisc-elem-create-desc" />
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-xs font-bold text-slate-600 hover:bg-gray-50">Cancelar</button>
                <button onClick={createCriteria} disabled={creating} className="px-4 py-2 rounded-lg bg-[#0A3747] text-[#E0E621] text-xs font-bold disabled:opacity-60 flex items-center gap-1.5"
                  data-testid="fisc-elem-create-submit">
                  {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Crear
                </button>
              </div>
            </div>
          </div>
        )}

        {!selected && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <ClipboardList size={36} className="mb-3 opacity-50" />
            <div className="text-sm font-bold">Selecciona un Elemento</div>
            <div className="text-[11px] mt-1">Elige de la lista para ver su detalle, fotos y reglas asociadas</div>
          </div>
        )}

        {selected && detailLoading && <div className="text-center py-16"><Loader2 size={18} className="animate-spin inline mr-2" /> Cargando…</div>}

        {selected && detail && !detailLoading && (
          <div className="flex flex-col h-full">
            {/* Header del elemento */}
            <div className="px-5 py-3 bg-slate-50 border-b border-gray-200 flex items-center gap-3">
              <div className="flex-1">
                <div className="text-sm font-bold text-[#0A3747] flex items-center gap-2">
                  <span className="bg-[#0A3747] text-white text-[9px] font-bold rounded px-1.5 py-0.5">#{detail.id}</span>
                  {editing ? (
                    <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                      className="flex-1 px-2 py-1 rounded border border-[#E0E621] text-sm font-bold bg-white" data-testid="fisc-elem-edit-name" />
                  ) : detail.name}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {detail.is_shared && (
                  <span className="bg-amber-100 text-amber-700 text-[9px] font-bold rounded-full px-2.5 py-1" data-testid="fisc-elem-shared-badge">
                    COMPARTIDO · {detail.solution_count} tipos
                  </span>
                )}
                {!editing ? (
                  <>
                    <button onClick={startEdit} className="px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-[10px] font-bold text-[#0A3747] hover:bg-gray-50" data-testid="fisc-elem-edit-btn">Editar</button>
                    <button onClick={() => deleteCriteria(detail.id)} className="px-3 py-1.5 rounded-lg bg-white border border-red-200 text-[10px] font-bold text-red-600 hover:bg-red-50" data-testid="fisc-elem-delete-btn">
                      <Trash2 size={11} className="inline mr-1" />Eliminar
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-[10px] font-bold text-slate-600">Cancelar</button>
                    <button onClick={saveEdit} disabled={saving} className="px-3 py-1.5 rounded-lg bg-[#0A3747] text-[#E0E621] text-[10px] font-bold disabled:opacity-60 flex items-center gap-1" data-testid="fisc-elem-save-btn">
                      {saving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Guardar
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Aviso de compartido */}
            {detail.is_shared && (
              <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 text-[10px] text-amber-700" data-testid="fisc-elem-shared-warning">
                Usado por: <strong>{(detail.solution_names || []).join(", ")}</strong>. Los cambios se reflejan en todos.
              </div>
            )}

            <div className="overflow-y-auto flex-1 p-5 space-y-5">
              {/* Sección: Info */}
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-gray-100 pb-1">Definición del Criterio</div>
                {editing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { key: "que_debe_validar", label: "Qué debe validar", rows: 2 },
                      { key: "parametro_umbral", label: "Parámetro / Umbral" },
                      { key: "si_cumple", label: "Si cumple" },
                      { key: "si_no_cumple", label: "Si no cumple" },
                      { key: "si_no_verificable", label: "Si no verificable" },
                      { key: "excepciones", label: "Excepciones" },
                      { key: "dependencia", label: "Dependencia" },
                    ].map(f => (
                      <label key={f.key} className={f.rows ? "md:col-span-2" : ""}>
                        <div className="text-[9px] font-bold text-slate-500 mb-0.5">{f.label}</div>
                        {f.rows ? (
                          <textarea value={editForm[f.key] || ""} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                            rows={f.rows} className="w-full px-2 py-1.5 rounded border border-gray-300 text-[11px]" />
                        ) : (
                          <input value={editForm[f.key] || ""} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                            className="w-full px-2 py-1.5 rounded border border-gray-300 text-[11px]" />
                        )}
                      </label>
                    ))}
                    <label>
                      <div className="text-[9px] font-bold text-slate-500 mb-0.5">Reintentos default</div>
                      <input type="number" value={editForm.reintentos_default ?? 3} onChange={e => setEditForm(p => ({ ...p, reintentos_default: parseInt(e.target.value) || 3 }))}
                        className="w-24 px-2 py-1.5 rounded border border-gray-300 text-[11px]" />
                    </label>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {[
                      { label: "Qué debe validar", value: detail.que_debe_validar },
                      { label: "Parámetro / Umbral", value: detail.parametro_umbral },
                      { label: "Si cumple", value: detail.si_cumple },
                      { label: "Si no cumple", value: detail.si_no_cumple },
                      { label: "Si no verificable", value: detail.si_no_verificable },
                      { label: "Excepciones", value: detail.excepciones },
                      { label: "Dependencia", value: detail.dependencia },
                      { label: "Reintentos", value: detail.reintentos_default },
                    ].map((f, i) => (
                      <div key={i}>
                        <div className="text-[9px] font-bold text-slate-400 uppercase">{f.label}</div>
                        <div className="text-[11px] text-[#0A3747]">{f.value || <span className="text-slate-300 italic">—</span>}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sección: Fotos de Referencia */}
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-gray-100 pb-1 flex items-center gap-2">
                  <Camera size={12} /> Fotos de Referencia ({(detail.reference_photo_urls || []).filter(Boolean).length}/3)
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[0, 1, 2].map(slot => {
                    const url = getSlotUrl(detail.reference_photo_urls, slot);
                    const isUploading = uploading === `${detail.id}-${slot}`;
                    const inputKey = `elem-${detail.id}-${slot}`;
                    return (
                      <div key={slot} className="relative">
                        <div className="aspect-video bg-slate-50 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                          {url ? (
                            <>
                              <img src={url} alt={`Ref ${slot + 1}`} className="w-full h-full object-cover" />
                              <button onClick={() => deletePhoto(detail.id, slot)}
                                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-red-600 z-10"
                                data-testid={`fisc-elem-photo-del-${slot}`}><X size={10} /></button>
                            </>
                          ) : (
                            <div className="text-gray-300 flex flex-col items-center gap-0.5 text-[9px]">
                              <ImageIcon size={18} /><span>Foto {slot + 1}</span>
                            </div>
                          )}
                          {isUploading && <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg"><Loader2 className="animate-spin" size={16} /></div>}
                        </div>
                        <input type="file" accept="image/*" className="hidden" ref={el => (inputRefs.current[inputKey] = el)} onChange={e => uploadPhoto(detail.id, slot, e.target.files?.[0])} />
                        <button onClick={() => inputRefs.current[inputKey]?.click()} disabled={isUploading}
                          className="w-full mt-1.5 px-1 py-1.5 rounded-lg bg-[#0A3747] text-[#E0E621] text-[9px] font-bold flex items-center justify-center gap-1 disabled:opacity-60 hover:bg-[#0c4a5f] transition"
                          data-testid={`fisc-elem-photo-upload-${slot}`}>
                          <Upload size={9} /> {url ? "Cambiar" : "Subir"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sección: Reglas / Soluciones asociadas */}
              {(detail.solution_names?.length > 0 || detail.rule_ids?.length > 0) && (
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-gray-100 pb-1">
                    Tipos de Solución Asociados ({detail.solution_count || 0})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(detail.solution_names || []).map((sn, i) => (
                      <span key={i} className="bg-slate-100 text-[#0A3747] text-[10px] font-bold rounded-full px-2.5 py-1">{sn}</span>
                    ))}
                  </div>
                  {detail.rule_ids && <div className="text-[9px] text-slate-400">{detail.rule_ids.length} regla(s) activa(s)</div>}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// TAB 3 · Tipos de Aprovisionamiento (v57.1.0)
// ──────────────────────────────────────────────────────────────────
// v101.155.0 · El tipo de aprovisionamiento solo dice QUÉ hace; la red (fibra / radio) la pone el tipo de solución
const PFT_ACCIONES = [
  { v: "instalacion", l: "Instalación", f: "fibra 1 · radio 4" },
  { v: "traslado", l: "Traslado", f: "fibra 2 · radio 5" },
  { v: "cambio_equipo", l: "Cambio de equipo", f: "fibra 3 · radio 6" },
  { v: "verificacion", l: "Verificación técnica", f: "flujo 7" },
  { v: "", l: "Sin aprovisionamiento", f: "" },
];

function ProvisioningFlowTypesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openUse, setOpenUse] = useState(null);   // v101.141.0 · tipos de solución que usan el tipo
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ code: "", name: "", description: "", active: true, radius_m: "", client_types: [], accion: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/fisc-matrix/provisioning-flow-types");
      setItems(data.items || []);
    } catch { toast.error("Error cargando tipos de aprovisionamiento"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setFormData({ code: "", name: "", description: "", active: true, radius_m: "", client_types: [], accion: "" });
    setEditing(null);
    setShowForm(false);
  };

  const startCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (item) => {
    setFormData({
      code: item.code || "",
      name: item.name || "",
      description: item.description || "",
      active: item.active !== false,
      radius_m: item.config?.radius_m ?? "",
      accion: item.accion || "",
    });
    setEditing(item);
    setShowForm(true);
  };

  const saveItem = async () => {
    if (!formData.name.trim()) return toast.error("El nombre es obligatorio");
    if (!editing && !formData.code.trim()) return toast.error("El código es obligatorio");
    setSaving(true);
    try {
      const config = {};
      if (formData.radius_m !== "" && formData.radius_m !== null) {
        config.radius_m = Number(formData.radius_m);
      } else if (editing) {
        config.radius_m = null;   // v101.135.0 · vaciar el campo borra el valor guardado
      }
      // v101.141.0 · El alcance (tipo de cliente y red) lo define el tipo de solución; aquí ya no se edita
      // v101.155.0 · Qué hace el flujo. La red NO se guarda aquí: la pone la «Tecnología» del tipo de solución.
      //            (flujo base guardado solo como respaldo si se apaga la validación «Red según el tipo de solución»)
      const FB = { instalacion: [1, 4], traslado: [2, 5], cambio_equipo: [3, 6] };
      if (formData.accion) {
        config.tipo_solucion = formData.accion;
        config.flujo = formData.accion === "verificacion" ? 7
          : FB[formData.accion][(editing?.config?.red === "radio_enlace") ? 1 : 0];
      } else if (editing && editing.accion) {
        config.tipo_solucion = null;
        config.flujo = null;
      }
      if (editing) {
        await api.patch(`/v1/fisc-matrix/provisioning-flow-types/${editing.id}`, {
          name: formData.name,
          description: formData.description,
          active: formData.active,
          config,
        });
        toast.success("Tipo actualizado");
      } else {
        await api.post("/v1/fisc-matrix/provisioning-flow-types", {
          code: formData.code,
          name: formData.name,
          description: formData.description,
          active: formData.active,
          config,
        });
        toast.success("Tipo creado");
      }
      resetForm();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const deleteItem = async (item) => {
    if (item.usage_count > 0) {
      toast.error(`No se puede eliminar: ${item.usage_count} tipo(s) de solución lo usan`);
      return;
    }
    if (!window.confirm(`¿Eliminar "${item.name}"?`)) return;
    try {
      await api.delete(`/v1/fisc-matrix/provisioning-flow-types/${item.id}`);
      toast.success("Tipo eliminado");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al eliminar"); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="provisioning-flow-types-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-[#0A3747]">Catálogo de Tipos de Aprovisionamiento</h2>
          <p className="text-[11px] text-slate-500">
            Qué se hace al aprovisionar. La red (fibra / radio) y a quién aplica los pone el tipo de solución.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="px-3 py-1.5 rounded-lg bg-[#0A3747] text-[#E0E621] text-xs font-bold flex items-center gap-1.5"
          data-testid="pft-create-btn"
        >
          <Plus size={12} /> Nuevo Tipo
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pft-form-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
            <div className="text-sm font-bold text-[#0A3747]">
              {editing ? "Editar Tipo de Aprovisionamiento" : "Nuevo Tipo de Aprovisionamiento"}
            </div>
            <div className="space-y-3">
              {!editing && (
                <label className="block">
                  <div className="text-[10px] font-bold text-slate-500 mb-1">Código (único, snake_case) *</div>
                  <input
                    value={formData.code}
                    onChange={e => setFormData(p => ({ ...p, code: e.target.value.toLowerCase().replace(/\s+/g, "_") }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                    placeholder="ej: traslado_fibra"
                    data-testid="pft-form-code"
                  />
                </label>
              )}
              <label className="block">
                <div className="text-[10px] font-bold text-slate-500 mb-1">Nombre *</div>
                <input
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                  placeholder="Ej: Instalación Fibra Auto"
                  data-testid="pft-form-name"
                />
              </label>
              <label className="block">
                <div className="text-[10px] font-bold text-slate-500 mb-1">Descripción</div>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm resize-none"
                  rows={2}
                  placeholder="Descripción del flujo"
                  data-testid="pft-form-desc"
                />
              </label>
              <label className="block">
                <div className="text-[10px] font-bold text-slate-500 mb-1">¿Qué hace? *</div>
                <select
                  value={formData.accion}
                  onChange={e => setFormData(p => ({ ...p, accion: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                  data-testid="pft-form-accion"
                >
                  {PFT_ACCIONES.map(a => <option key={a.v} value={a.v}>{a.l}{a.f ? ` (${a.f})` : ""}</option>)}
                </select>
                <div className="text-[9px] text-slate-400 mt-0.5">
                  La red (fibra / radio) NO se elige aquí: la pone la «Tecnología» del tipo de solución que use este tipo
                  (o el plan de la OT si el tipo de solución no la tiene).
                </div>
              </label>
              <label className="block">
                <div className="text-[10px] font-bold text-slate-500 mb-1">Radio de búsqueda de cajas · fibra (metros)</div>
                <input
                  type="number"
                  value={formData.radius_m}
                  onChange={e => setFormData(p => ({ ...p, radius_m: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                  placeholder="1000"
                  min={0}
                  max={50000}
                  data-testid="pft-form-radius"
                />
                <div className="text-[9px] text-slate-400 mt-0.5">
                  Se usa en nearby-boxes para buscar cajas cercanas (0 = no aplica)
                </div>
              </label>
              {/* v101.141.0 · El alcance ya no se configura aquí */}
              <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-[10.5px] text-blue-800" data-testid="pft-form-scope-info">
                <b>¿A qué OTs y a qué red aplica?</b> Lo define el <b>tipo de solución</b> que tiene asignado este tipo: su
                «Aplica a» (tipo de cliente del contrato) y su «Tecnología» (fibra / radio), en Tipos de Solución. Este
                catálogo solo define QUÉ se hace.
                {(editing?.usage_nodes || []).length > 0 && (
                  <div className="mt-1 text-blue-700">Lo usan: {editing.usage_nodes.map(u => u.ruta).join(" · ")}</div>
                )}
              </div>
              <div className="flex items-center justify-between py-1">
                <div className="text-[10px] font-bold text-slate-500">Activo</div>
                <Switch
                  checked={formData.active}
                  onCheckedChange={v => setFormData(p => ({ ...p, active: v }))}
                  data-testid="pft-form-active"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={resetForm} className="px-4 py-2 rounded-lg border border-gray-300 text-xs font-bold text-slate-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={saveItem} disabled={saving} className="px-4 py-2 rounded-lg bg-[#0A3747] text-[#E0E621] text-xs font-bold disabled:opacity-60 flex items-center gap-1.5" data-testid="pft-form-submit">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                {editing ? "Guardar" : "Crear"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500"><Loader2 size={16} className="animate-spin inline mr-2" /> Cargando...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-400 italic text-xs">No hay tipos de aprovisionamiento definidos</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="pft-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="px-4 py-2.5 text-left font-bold">Código</th>
                <th className="px-4 py-2.5 text-left font-bold">Nombre</th>
                <th className="px-4 py-2.5 text-left font-bold">Qué hace</th>
                <th className="px-4 py-2.5 text-center font-bold">En uso</th>
                <th className="px-4 py-2.5 text-center font-bold">Estado</th>
                <th className="px-4 py-2.5 text-right font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(item => (
                <Fragment key={item.id}>
                <tr className="hover:bg-slate-50/50 transition-colors" data-testid={`pft-row-${item.code}`}>
                  <td className="px-4 py-2.5 font-mono text-[#0A3747] font-bold">{item.code}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-bold text-[#0A3747]">{item.name}</div>
                    {item.description && <div className="text-[10px] text-slate-400 mt-0.5">{item.description}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    {/* v101.141.0 · Solo lo que define el flujo; el alcance (segmentos, planes, tipos de cliente) lo
                        decide el tipo de solución */}
                    {/* v101.155.0 · Qué hace + flujo según la red del tipo de solución (la red ya no es del catálogo) */}
                    {item.accion ? (
                      <div className="flex flex-wrap gap-1" data-testid={`pft-accion-${item.code}`}>
                        <span className="bg-blue-50 text-blue-700 text-[10px] px-2 py-0.5 rounded-full font-bold">{item.accion_label || item.accion}</span>
                        {item.flujos_por_red && (
                          <span className="bg-slate-50 text-slate-600 text-[10px] px-2 py-0.5 rounded-full font-mono">
                            fibra → {item.flujos_por_red.fibra_optica} · radio → {item.flujos_por_red.radio_enlace}
                          </span>
                        )}
                        {item.config?.radius_m ? (
                          <span className="bg-slate-50 text-slate-600 text-[10px] px-2 py-0.5 rounded-full font-mono">radio búsqueda: {item.config.radius_m} m</span>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-slate-400 italic">Sin aprovisionamiento</span>
                    )}
                    <div className="text-[9.5px] text-slate-400 mt-1">Red y alcance: según el tipo de solución</div>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {item.usage_count > 0 ? (
                      <button type="button" onClick={() => setOpenUse(openUse === item.id ? null : item.id)}
                        title="Ver los tipos de solución que lo usan" data-testid={`pft-usage-${item.code}`}
                        className="bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full hover:bg-amber-100">
                        {item.usage_count}
                      </button>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                      {item.active ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => startEdit(item)}
                        className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-[#0A3747]"
                        title="Editar"
                        data-testid={`pft-edit-${item.code}`}
                      ><Pencil size={13} /></button>
                      <button
                        onClick={() => deleteItem(item)}
                        className={`p-1 rounded hover:bg-red-50 ${item.usage_count > 0 ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:text-red-600"}`}
                        title={item.usage_count > 0 ? "En uso, no se puede eliminar" : "Eliminar"}
                        disabled={item.usage_count > 0}
                        data-testid={`pft-delete-${item.code}`}
                      ><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
                {openUse === item.id && (
                  <tr key={`${item.id}-use`} className="bg-amber-50/40" data-testid={`pft-usage-list-${item.code}`}>
                    <td colSpan={6} className="px-4 py-2 text-[10.5px] text-slate-600">
                      <b>Tipos de solución con este flujo:</b>
                      <ul className="mt-1 space-y-0.5">
                        {(item.usage_nodes || []).map(u => (
                          <li key={u.node_id} className="font-mono">{u.ruta}{u.odoo_activity_type ? ` · ${u.odoo_activity_type}` : ""}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// TAB 4 · Catálogo de Integraciones (v64.0.0 · solo lectura)
// ═══════════════════════════════════════════════════════════════
const CATEGORY_LABELS = {
  infraestructura: { label: "Infraestructura", bg: "#F0FDF4", fg: "#166534", border: "#BBF7D0" },
  provisioning: { label: "Aprovisionamiento", bg: "#EFF6FF", fg: "#1E40AF", border: "#BFDBFE" },
};

function IntegrationsCatalogTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [peStats, setPeStats] = useState(null);
  // v86 · 3 syncs independientes
  const [syncStates, setSyncStates] = useState({
    vlans: { syncing: false, result: null, stats: null },
    ips: { syncing: false, result: null, stats: null },
    rels: { syncing: false, result: null, stats: null },
  });

  const SYNC_CONFIGS = [
    { key: "vlans", label: "VLANs", desc: "Sync incremental: UPSERT + desactiva obsoletas. No borra datos existentes.",
      forceUrl: "/v1/admin/sync/vlans/force", statusUrl: "/v1/admin/sync/vlans/status", color: "#0E7490" },
    { key: "ips", label: "IPs", desc: "Sync incremental paginado (500/página) con checkpoint. Retoma desde corte.",
      forceUrl: "/v1/admin/sync/ips/force", statusUrl: "/v1/admin/sync/ips/status", color: "#7C3AED" },
    { key: "rels", label: "Relaciones OLT-VLAN", desc: "Sync incremental: UPSERT + limpieza de pares obsoletos.",
      forceUrl: "/v1/admin/sync/olt-vlan-rels/force", statusUrl: "/v1/admin/sync/olt-vlan-rels/status", color: "#B45309" },
  ];

  const fetchPeStats = async () => {
    try {
      const { data } = await api.get("/v1/admin/sync/passive-equipment/status");
      setPeStats(data);
    } catch { /* ignore */ }
  };

  const fetchSyncStats = async () => {
    for (const cfg of SYNC_CONFIGS) {
      try {
        const { data } = await api.get(cfg.statusUrl);
        setSyncStates(prev => ({
          ...prev,
          [cfg.key]: { ...prev[cfg.key], stats: data },
        }));
      } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/v1/provisioning-audit/integrations");
        setItems(data.items || []);
      } catch {
        toast.error("Error cargando catálogo de integraciones");
      } finally {
        setLoading(false);
      }
    })();
    fetchPeStats();
    fetchSyncStats();
  }, []);

  const handleSyncCajas = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data: started } = await api.post("/v1/admin/sync/passive-equipment/force");
      toast.info(started?.status === "already_running"
        ? "Ya hay una sincronización de cajas en curso; mostrando su avance…"
        : "Sincronización de cajas iniciada (lotes de 500)…");
      const poll = async () => {
        for (let i = 0; i < 360; i++) {
          await new Promise((r) => setTimeout(r, 5000));
          try {
            const { data: st } = await api.get("/v1/admin/sync/passive-equipment/status");
            setPeStats(st);
            if (st.status !== "running") {
              setSyncing(false);
              setPeStats(st);
              if (st.status === "ok" || st.status === "done") {
                const msg = `${st.rows_upserted} cajas actualizadas. GPS: ${st.with_gps}/${st.total_rows} (${st.gps_coverage_pct}%)`;
                toast.success(msg);
                setSyncResult({ ok: true, msg });
              } else if (st.status === "partial_ok") {
                const msg = `Parcial: ${st.rows_upserted} cajas guardadas antes del corte. GPS: ${st.with_gps}/${st.total_rows}`;
                toast.warning(msg);
                setSyncResult({ ok: true, msg, partial: true });
              } else if (st.status === "idle" || st.interrupted) {
                // v100.7.0 · El servidor se reinició a mitad del proceso: el avance quedó guardado
                const msg = `El proceso se interrumpió (reinicio del servidor). Lo avanzado quedó guardado${st.fill_last_id ? ` (hasta el equipo #${st.fill_last_id})` : ""}: vuelva a pulsar "Sincronizar" y continuará desde ahí.`;
                toast.warning(msg);
                setSyncResult({ ok: false, msg, partial: true });
              } else {
                const msg = st.error || st.skipped || `Terminó con estado "${st.status}"`;
                toast.error(`Sync falló: ${msg}`);
                setSyncResult({ ok: false, msg });
              }
              return;
            }
          } catch { /* retry */ }
        }
        setSyncing(false);
        fetchPeStats();
        setSyncResult({ ok: true, partial: true, msg: "La sincronización sigue en segundo plano. Puede salir de esta pantalla; vuelva luego para ver el resultado." });
      };
      poll();
    } catch (e) {
      setSyncing(false);
      toast.error("Error al iniciar sync: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleSyncIndividual = async (cfg) => {
    const { key, label, forceUrl, statusUrl } = cfg;
    setSyncStates(prev => ({
      ...prev,
      [key]: { ...prev[key], syncing: true, result: null },
    }));
    try {
      const resp = await api.post(forceUrl);
      if (resp.data?.status === "already_running") {
        toast.warning(`Ya hay una sincronización de ${label} en curso.`);
        setSyncStates(prev => ({
          ...prev,
          [key]: { ...prev[key], syncing: false, result: { ok: true, msg: resp.data.progress || "Sync en curso...", partial: true } },
        }));
        return;
      }
      toast.info(`Sync de ${label} iniciado en background...`);
      const poll = async () => {
        for (let i = 0; i < 120; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const { data: st } = await api.get(statusUrl);
            setSyncStates(prev => ({
              ...prev,
              [key]: { ...prev[key], stats: st },
            }));
            if (st.progress && st.status === "running") {
              setSyncStates(prev => ({
                ...prev,
                [key]: { ...prev[key], result: { ok: true, msg: st.progress, partial: true } },
              }));
            }
            if (st.status !== "running") {
              if (st.status === "ok" || st.status === "done") {
                const count = st.current_count ?? st.after ?? 0;
                const errs = st.result?.ip_errors || st.result?.rel_errors || 0;
                let msg = `${label}: ${typeof count === 'object' ? JSON.stringify(count) : count} registros sincronizados`;
                if (errs > 0) msg += ` (${errs} con error)`;
                toast.success(msg);
                setSyncStates(prev => ({
                  ...prev,
                  [key]: { ...prev[key], syncing: false, result: { ok: true, msg } },
                }));
              } else {
                const msg = st.error || st.result?.error || "Error desconocido";
                toast.error(`Sync ${label} falló: ${msg}`);
                setSyncStates(prev => ({
                  ...prev,
                  [key]: { ...prev[key], syncing: false, result: { ok: false, msg } },
                }));
              }
              return;
            }
          } catch { /* retry */ }
        }
        // v101.28.0 · Pasados 6 min la sincronización SIGUE en segundo plano: no es un error
        setSyncStates(prev => ({
          ...prev,
          [key]: { ...prev[key], syncing: false, result: { ok: true, partial: true, msg: "Sigue en segundo plano. Pulse el botón otra vez más tarde para ver el resultado (no se duplica: si ya está corriendo, solo informa)." } },
        }));
        fetchSyncStats();
      };
      poll();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.warning(e.response.data?.message || `Ya hay un sync de ${label} en curso.`);
        setSyncStates(prev => ({
          ...prev,
          [key]: { ...prev[key], syncing: false, result: { ok: true, msg: e.response.data?.message || "Sync en curso", partial: true } },
        }));
      } else {
        toast.error(`Error al iniciar sync ${label}: ` + (e.response?.data?.detail || e.message));
        setSyncStates(prev => ({
          ...prev,
          [key]: { ...prev[key], syncing: false },
        }));
      }
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" size={24} /></div>;

  return (
    <div className="space-y-6" data-testid="integrations-catalog-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-[#0A3747]">Catálogo de Integraciones de Aprovisionamiento</h2>
        <span className="text-[10px] font-mono text-gray-400">{items.length} integraciones</span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => {
          const cat = CATEGORY_LABELS[it.category] || { label: it.category, bg: "#F3F4F6", fg: "#374151", border: "#E5E7EB" };
          return (
            <div
              key={it.id}
              className="rounded-xl border p-4 flex flex-col gap-2 transition hover:shadow-md"
              style={{ borderColor: cat.border, background: "white" }}
              data-testid={`integration-card-${it.code}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#0A3747]">{it.name}</span>
                {it.active
                  ? <CheckCircle2 size={14} className="text-emerald-500" />
                  : <XCircle size={14} className="text-gray-300" />}
              </div>
              <div className="flex items-center gap-2">
                <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{it.code}</code>
                <span
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: cat.bg, color: cat.fg }}
                >
                  {cat.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {items.length === 0 && (
        <div className="text-center py-10 text-sm text-gray-400 italic">
          No hay integraciones configuradas.
        </div>
      )}

      {/* v67 · Sync Manual Cajas Odoo */}
      <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xs font-bold text-[#0A3747]">Sincronizar Cajas Odoo</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">
              Sync incremental por checkpoints — retoma desde el último lote exitoso.
            </p>
          </div>
          <button
            onClick={handleSyncCajas}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition disabled:opacity-50"
            style={{ background: syncing ? "#94A3B8" : "#0A3747", color: "white" }}
            data-testid="btn-sync-cajas-odoo"
          >
            {syncing
              ? <><Loader2 size={13} className="animate-spin" /> Sincronizando...</>
              : <><Activity size={13} /> Sincronizar Cajas Odoo</>}
          </button>
        </div>

        {/* v70 · Panel de estado */}
        {peStats && (
          <div className="grid grid-cols-3 gap-2" data-testid="pe-sync-stats">
            <div className="rounded-lg bg-white border border-gray-200 p-2.5 text-center">
              <p className="text-[10px] text-gray-500 mb-0.5">Cobertura GPS</p>
              <p className="text-sm font-bold text-[#0A3747]" data-testid="pe-gps-coverage">
                {peStats.gps_coverage_pct ?? 0}%
              </p>
              <p className="text-[9px] text-gray-400">{peStats.with_gps?.toLocaleString()} / {peStats.total_rows?.toLocaleString()}</p>
            </div>
            <div className="rounded-lg bg-white border border-gray-200 p-2.5 text-center">
              <p className="text-[10px] text-gray-500 mb-0.5">Último Lote</p>
              <p className="text-[11px] font-semibold text-[#0A3747]" data-testid="pe-last-batch">
                {peStats.last_successful_batch
                  ? new Date(peStats.last_successful_batch).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "short" })
                  : "—"}
              </p>
            </div>
            <div className="rounded-lg bg-white border border-gray-200 p-2.5 text-center">
              <p className="text-[10px] text-gray-500 mb-0.5">Estado de Red</p>
              <p className="text-[11px] font-bold" data-testid="pe-network-status" style={{
                color: peStats.network_status === "connected" ? "#059669"
                     : peStats.network_status === "blocked" ? "#DC2626"
                     : "#6B7280"
              }}>
                {peStats.network_status === "connected" ? "Conectado"
                 : peStats.network_status === "blocked" ? "Bloqueado"
                 : peStats.network_status === "no_engine" ? "Sin Config"
                 : "Desconocido"}
              </p>
            </div>
          </div>
        )}

        {syncResult && (
          <div
            className="rounded-lg px-3 py-2 text-xs font-medium"
            style={{
              background: syncResult.ok ? (syncResult.partial ? "#FEF3C7" : "#D1FAE5") : "#FEE2E2",
              color: syncResult.ok ? (syncResult.partial ? "#92400E" : "#065F46") : "#991B1B",
            }}
            data-testid="sync-result-banner"
          >
            {syncResult.ok ? <CheckCircle2 size={13} className="inline mr-1" /> : <XCircle size={13} className="inline mr-1" />}
            {syncResult.msg}
          </div>
        )}
      </div>

      {/* v86 · 3 Syncs Independientes: VLANs, IPs, Relaciones */}
      <div className="mt-4 space-y-3">
        <div className="flex items-center gap-2 px-1">
          <Activity size={14} style={{ color: "#0A3747" }} />
          <span className="text-xs font-bold text-[#0A3747]">Sincronización Odoo — VLANs / IPs / Relaciones</span>
        </div>
        <p className="text-[10px] text-gray-500 px-1">
          Sync no destructivo: UPSERT incremental + limpieza de obsoletos al final.
          IPs paginadas en bloques de 500 con checkpoint para retomar tras corte.
        </p>
        {/* v101.45.0 · Relleno de VLANs → IPs → relaciones (vista previa + avance) */}
        <InfraRefillCard />
        {SYNC_CONFIGS.map(cfg => {
          const st = syncStates[cfg.key];
          return (
            <div key={cfg.key} className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3" data-testid={`sync-block-${cfg.key}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold" style={{ color: cfg.color }}>{cfg.label}</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">{cfg.desc}</p>
                </div>
                <button
                  onClick={() => handleSyncIndividual(cfg)}
                  disabled={st.syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition disabled:opacity-50"
                  style={{ background: st.syncing ? "#94A3B8" : cfg.color, color: "white" }}
                  data-testid={`btn-sync-${cfg.key}`}
                >
                  {st.syncing
                    ? <><Loader2 size={13} className="animate-spin" /> Sincronizando...</>
                    : <><Activity size={13} /> Sincronizar {cfg.label}</>}
                </button>
                {cfg.key === "ips" && (
                  <button
                    onClick={async () => {
                      if (!window.confirm("Esto borrará el incremento y recargará TODAS las IPs desde Odoo. ¿Continuar?")) return;
                      setSyncStates(prev => ({ ...prev, ips: { ...prev.ips, syncing: true } }));
                      try {
                        const { data } = await api.post("/v1/admin/sync/ips/force-full");
                        toast.success(data.message || "Llenado masivo iniciado");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Error al iniciar llenado masivo");
                        setSyncStates(prev => ({ ...prev, ips: { ...prev.ips, syncing: false } }));
                      }
                    }}
                    disabled={st.syncing}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition disabled:opacity-50"
                    style={{ background: st.syncing ? "#94A3B8" : "#DC2626", color: "white" }}
                    data-testid="btn-sync-ips-full"
                  >
                    <RefreshCw size={13} /> Llenado masivo
                  </button>
                )}
              </div>
              {st.stats && (
                <div className="flex items-center gap-3" data-testid={`sync-stats-${cfg.key}`}>
                  <div className="rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-center">
                    <p className="text-[10px] text-gray-500">Registros</p>
                    <p className="text-sm font-bold" style={{ color: cfg.color }} data-testid={`sync-count-${cfg.key}`}>
                      {(st.stats.current_count ?? 0).toLocaleString()}
                    </p>
                  </div>
                  {st.stats.status && st.stats.status !== "not_run" && (
                    <div className="rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-center">
                      <p className="text-[10px] text-gray-500">Estado</p>
                      <p className={`text-[11px] font-bold ${st.stats.status === "ok" || st.stats.status === "done" ? "text-green-700" : st.stats.status === "running" ? "text-blue-600" : "text-red-600"}`}>
                        {st.stats.status === "ok" ? "OK" : st.stats.status === "running" ? "Corriendo..." : st.stats.status === "not_run" ? "Sin ejecutar" : st.stats.status}
                      </p>
                    </div>
                  )}
                  {st.stats.progress && st.stats.status === "running" && (
                    <div className="flex-1 text-[10px] text-blue-600 font-medium truncate">
                      {st.stats.progress}
                    </div>
                  )}
                </div>
              )}
              {st.result && (
                <div
                  className="rounded-lg px-3 py-2 text-xs font-medium"
                  style={{
                    background: st.result.ok ? "#D1FAE5" : "#FEE2E2",
                    color: st.result.ok ? "#065F46" : "#991B1B",
                  }}
                  data-testid={`sync-result-${cfg.key}`}
                >
                  {st.result.ok ? <CheckCircle2 size={13} className="inline mr-1" /> : <XCircle size={13} className="inline mr-1" />}
                  {st.result.msg}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
